import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const Home = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("home");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Update active section based on scroll position
      const sections = ["home", "about", "services", "contact"];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setActiveSection(sectionId);
      setIsMobileMenuOpen(false);
    }
  };

  // Updated Header Styles
  const headerStyles = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: windowWidth > 768 ? "1.5rem 5%" : "1rem 5%",
    backgroundColor: isScrolled
      ? "rgba(10, 10, 10, 0.95)"
      : "rgba(10, 10, 10, 0.9)",
    backdropFilter: "blur(10px)",
    zIndex: 1000,
    transition: "all 0.3s ease",
    borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
    boxShadow: isScrolled ? "0 4px 30px rgba(0, 0, 0, 0.5)" : "none",
  };

  // Updated Mobile Menu Styles
  const mobileNavStyles = {
    position: "fixed",
    top: windowWidth > 768 ? "80px" : "70px",
    right: "20px",
    backgroundColor: "#1a1a1a",
    borderRadius: "10px",
    padding: "2rem",
    display: "flex",
    flexDirection: "column",
    gap: "1.5rem",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
    border: "1px solid rgba(255, 255, 255, 0.05)",
    animation: "slideDown 0.3s ease-out",
    width: windowWidth <= 480 ? "calc(100% - 40px)" : "350px",
    maxWidth: "90vw",
  };

  return (
    <div style={styles.mainContainer}>
      {/* Improved Header */}
      <header style={headerStyles}>
        <div style={styles.logo}>
          <span style={styles.logoAccent}>Bloo</span>pay
        </div>

        {/* Desktop Navigation - only shows on larger screens */}
        {windowWidth > 768 && (
          <nav style={styles.nav}>
            {["home", "about", "services", "contact"].map((section) => (
              <button
                key={section}
                style={{
                  ...styles.navButton,
                  ...(activeSection === section && styles.activeNavButton),
                }}
                onClick={() => scrollToSection(section)}
              >
                {section.charAt(0).toUpperCase() + section.slice(1)}
              </button>
            ))}
          </nav>
        )}

        {/* Auth buttons - only shows on larger screens */}
        {windowWidth > 768 && (
          <div style={styles.authButtons}>
            <button
              onClick={() => navigate("/login")}
              style={styles.loginButton}
            >
              Login
            </button>
            <button
              onClick={() => navigate("/register")}
              style={styles.registerButton}
            >
              Register
            </button>
          </div>
        )}

        {/* Mobile Menu Button - only shows on smaller screens */}
        {windowWidth <= 768 && (
          <button
            style={styles.mobileMenuButton}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <span style={styles.closeIcon}>✕</span>
            ) : (
              <span style={styles.menuIcon}>☰</span>
            )}
          </button>
        )}
      </header>

      {/* Mobile Navigation Drawer */}
      {isMobileMenuOpen && (
        <div style={styles.mobileNavOverlay}>
          <div style={mobileNavStyles}>
            {["home", "about", "services", "contact"].map((section) => (
              <button
                key={section}
                style={{
                  ...styles.mobileNavButton,
                  ...(activeSection === section &&
                    styles.activeMobileNavButton),
                }}
                onClick={() => scrollToSection(section)}
              >
                {section.charAt(0).toUpperCase() + section.slice(1)}
              </button>
            ))}
            <div style={styles.mobileAuthButtons}>
              <button
                style={styles.mobileLoginButton}
                onClick={() => navigate("/login")}
              >
                Login
              </button>
              <button
                style={styles.mobileRegisterButton}
                onClick={() => navigate("/register")}
              >
                Register
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rest of your sections remain the same */}
      {/* Hero Section */}
      {/* Hero Section */}
      <section id="home" style={styles.heroSection}>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>
            Unlock Your Potential:{" "}
            <span style={styles.heroTitleAccent}> Grow Your Network,</span> Earn
            More
          </h1>
          <p style={styles.heroSubtitle}>
            "Unlock Earnings by Building Your Network – Refer, Connect, and
            Grow!"
          </p>
          <div style={styles.heroButtons}>
            <button
              onClick={() => navigate("/register")}
              style={styles.primaryButton}
            >
              Get Started
            </button>
            <button
              style={styles.secondaryButton}
              onClick={() => scrollToSection("services")}
            >
              Explore Services
            </button>
          </div>
        </div>
        <div
          style={styles.scrollIndicator}
          onClick={() => scrollToSection("about")}
        >
          ↓
        </div>
      </section>

      {/* About Section */}
      <section id="about" style={styles.aboutSection}>
        <div style={styles.sectionContainer}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.sectionTitleAccent}>About</span> Us
          </h2>
          <div style={styles.aboutContent}>
            <div style={styles.aboutText}>
              <h3 style={styles.aboutHeading}>Our Vision</h3>
              <p style={styles.aboutParagraph}>
                We’re revolutionizing the way you earn online by combining
                cutting-edge technology with seamless design. Our goal is to
                make digital earning simple, engaging, and rewarding.
              </p>
              <p style={styles.aboutParagraph}>
                Launched in 2025, we’ve quickly evolved into a leading platform,
                empowering users worldwide with innovative earning solutions and
                a seamless digital experience.
              </p>
              <ul style={styles.featuresList}>
                <li style={styles.featureItem}>
                  <span style={styles.featureIcon}>✦</span> 150+ Successful
                  Projects
                </li>
                <li style={styles.featureItem}>
                  <span style={styles.featureIcon}>✦</span> 24/7 Premium Support
                </li>
                <li style={styles.featureItem}>
                  <span style={styles.featureIcon}>✦</span> Award-Winning Design
                </li>
              </ul>
            </div>
            <div style={styles.aboutImage}>
              <div style={styles.aboutImageWrapper}>
                <div style={styles.aboutImageOverlay} />
                <img
                  src="https://images.unsplash.com/photo-1579389083078-4e7018379f7e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80"
                  alt="Our Team"
                  style={styles.aboutImg}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" style={styles.servicesSection}>
        <div style={styles.sectionContainer}>
          <h2 style={styles.sectionTitle}>
            Our <span style={styles.sectionTitleAccent}>Services</span>
          </h2>
          <p style={styles.sectionSubtitle}>
            Providing a big chance to make your money by home
          </p>
          <div style={styles.servicesGrid}>
            <div style={styles.serviceCard}>
              <div style={styles.serviceIconWrapper}>
                <span style={styles.serviceIcon}>💻</span>
              </div>
              <h3 style={styles.serviceTitle}>Team Network</h3>
              <p style={styles.serviceDescription}>
                Build your team and make your money by doing nothing
              </p>
              <button style={styles.serviceLearnMore}>Learn More →</button>
            </div>
            <div style={styles.serviceCard}>
              <div style={styles.serviceIconWrapper}>
                <span style={styles.serviceIcon}>📱</span>
              </div>
              <h3 style={styles.serviceTitle}>Profitable Solutions</h3>
              <p style={styles.serviceDescription}>
                First month try to hard hell the life earn money by doing
                nothing a single effort
              </p>
              <button style={styles.serviceLearnMore}>Learn More →</button>
            </div>
            <div style={styles.serviceCard}>
              <div style={styles.serviceIconWrapper}>
                <span style={styles.serviceIcon}>☁️</span>
              </div>
              <h3 style={styles.serviceTitle}>Cloud Network</h3>
              <p style={styles.serviceDescription}>
                Network cloud help to earn money by using there own network
              </p>
              <button style={styles.serviceLearnMore}>Learn More →</button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={styles.contactSection}>
        <div style={styles.sectionContainer}>
          <h2 style={styles.sectionTitle}>
            Get In <span style={styles.sectionTitleAccent}>Touch</span>
          </h2>
          <p style={styles.sectionSubtitle}>
            Ready to start your earning? Reach out to our team today
          </p>
          <div style={styles.contactContent}>
            <form style={styles.contactForm}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Your Name</label>
                <input
                  type="text"
                  placeholder="Enter your name"
                  style={styles.formInput}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Your Email</label>
                <input
                  type="email"
                  placeholder="Enter your email"
                  style={styles.formInput}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Your Message</label>
                <textarea
                  placeholder="Tell us about your project"
                  style={styles.formTextarea}
                  rows="5"
                ></textarea>
              </div>
              <button type="submit" style={styles.submitButton}>
                Send Message
              </button>
            </form>
            <div style={styles.contactInfo}>
              <h3 style={styles.contactInfoTitle}>Contact Information</h3>
              <div style={styles.contactInfoItem}>
                <span style={styles.contactIcon}>✉️</span>
                <span>contact@BIoopay.com</span>
              </div>
              <div style={styles.contactInfoItem}>
                <span style={styles.contactIcon}>📞</span>
                <span>+1 (555) 123-4567</span>
              </div>
              <div style={styles.contactInfoItem}>
                <span style={styles.contactIcon}>🏢</span>
                <span>
                  456 Tech Avenue, Suite 200
                  <br />
                  Silicon Valley, CA 94025
                </span>
              </div>
              <div style={styles.socialLinks}>
                <a href="#" style={styles.socialLink}>
                  <span style={styles.socialIcon}>𝕏</span> Twitter
                </a>
                <a href="#" style={styles.socialLink}>
                  <span style={styles.socialIcon}>𝔽</span> Facebook
                </a>
                <a href="#" style={styles.socialLink}>
                  <span style={styles.socialIcon}>𝕃</span> LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <div style={styles.footerSection}>
            <h3 style={styles.footerLogo}>
              <span style={styles.logoAccent}>Bloo</span>Pay
            </h3>
            <p style={styles.footerText}>
              Transforming ideas into exceptional digital experiences with our
              signature dark aesthetic.
            </p>
          </div>
          <div style={styles.footerSection}>
            <h3 style={styles.footerHeading}>Quick Links</h3>
            <button
              style={styles.footerLink}
              onClick={() => scrollToSection("home")}
            >
              Home
            </button>
            <button
              style={styles.footerLink}
              onClick={() => scrollToSection("about")}
            >
              About
            </button>
            <button
              style={styles.footerLink}
              onClick={() => scrollToSection("services")}
            >
              Services
            </button>
            <button
              style={styles.footerLink}
              onClick={() => scrollToSection("contact")}
            >
              Contact
            </button>
          </div>
          <div style={styles.footerSection}>
            <h3 style={styles.footerHeading}>Legal</h3>
            <button style={styles.footerLink}>Privacy Policy</button>
            <button style={styles.footerLink}>Terms of Service</button>
            <button style={styles.footerLink}>Cookies</button>
          </div>
          <div style={styles.footerSection}>
            <h3 style={styles.footerHeading}>Newsletter</h3>
            <div style={styles.newsletterForm}>
              <input
                type="email"
                placeholder="Your email"
                style={styles.newsletterInput}
              />
              <button style={styles.newsletterButton}>Subscribe</button>
            </div>
          </div>
        </div>
        <div style={styles.footerBottom}>
          <p style={styles.footerBottomText}>
            © {new Date().getFullYear()} Bloopay. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

// Updated Styles with better responsiveness
const styles = {
  mainContainer: {
    fontFamily: "'Inter', sans-serif",
    backgroundColor: "#0a0a0a",
    color: "#e0e0e0",
    overflowX: "hidden",
    lineHeight: 1.6,
  },

  // Header Styles
  logo: {
    fontSize: "clamp(1.5rem, 4vw, 1.8rem)", // Responsive font size
    fontWeight: 700,
    background: "linear-gradient(135deg, #8a2be2 0%, #00bfff 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    display: "flex",
    alignItems: "center",
  },
  logoAccent: {
    fontWeight: 800,
    marginRight: "0.3rem",
  },
  nav: {
    display: "flex",
    gap: "1.5rem",
    "@media (max-width: 900px)": {
      gap: "1rem",
    },
  },
  navButton: {
    background: "none",
    border: "none",
    fontSize: "1rem",
    fontWeight: 500,
    color: "#b0b0b0",
    cursor: "pointer",
    padding: "0.5rem 0",
    transition: "all 0.3s ease",
    position: "relative",
    whiteSpace: "nowrap",
    ":hover": {
      color: "#ffffff",
    },
    "::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: 0,
      width: "0%",
      height: "2px",
      background: "linear-gradient(90deg, #8a2be2, #00bfff)",
      transition: "width 0.3s ease",
    },
    ":hover::after": {
      width: "100%",
    },
  },
  activeNavButton: {
    color: "#ffffff",
    "::after": {
      width: "100%",
    },
  },
  authButtons: {
    display: "flex",
    gap: "1rem",
  },
  loginButton: {
    padding: "0.6rem 1.5rem",
    background: "none",
    border: "1px solid rgba(138, 43, 226, 0.5)",
    borderRadius: "6px",
    color: "#b0b0b0",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.3s ease",
    whiteSpace: "nowrap",
    ":hover": {
      borderColor: "#8a2be2",
      color: "#ffffff",
      boxShadow: "0 0 15px rgba(138, 43, 226, 0.3)",
    },
    "@media (max-width: 900px)": {
      padding: "0.6rem 1rem",
    },
  },
  registerButton: {
    padding: "0.6rem 1.5rem",
    background: "linear-gradient(135deg, #8a2be2 0%, #00bfff 100%)",
    border: "none",
    borderRadius: "6px",
    color: "#ffffff",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.3s ease",
    whiteSpace: "nowrap",
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 5px 20px rgba(138, 43, 226, 0.5)",
    },
    "@media (max-width: 900px)": {
      padding: "0.6rem 1rem",
    },
  },
  mobileMenuButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "0.5rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  menuIcon: {
    fontSize: "1.8rem",
    color: "#b0b0b0",
    transition: "all 0.3s ease",
    ":hover": {
      color: "#ffffff",
    },
  },
  closeIcon: {
    fontSize: "1.8rem",
    color: "#b0b0b0",
    transition: "all 0.3s ease",
    ":hover": {
      color: "#ffffff",
    },
  },
  mobileNavOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    zIndex: 999,
    backdropFilter: "blur(5px)",
  },
  mobileNavButton: {
    padding: "0.8rem 1.5rem",
    background: "none",
    border: "none",
    textAlign: "left",
    fontSize: "1.1rem",
    color: "#b0b0b0",
    cursor: "pointer",
    transition: "all 0.3s ease",
    borderRadius: "5px",
    ":hover": {
      backgroundColor: "rgba(138, 43, 226, 0.1)",
      color: "#ffffff",
    },
  },
  activeMobileNavButton: {
    color: "#ffffff",
    background:
      "linear-gradient(135deg, rgba(138, 43, 226, 0.2) 0%, rgba(0, 191, 255, 0.2) 100%)",
    fontWeight: 500,
  },
  mobileAuthButtons: {
    display: "flex",
    gap: "1rem",
    paddingTop: "1rem",
    marginTop: "1rem",
    borderTop: "1px solid rgba(255, 255, 255, 0.05)",
  },
  mobileLoginButton: {
    flex: 1,
    padding: "0.8rem",
    background: "none",
    border: "1px solid rgba(138, 43, 226, 0.5)",
    borderRadius: "5px",
    color: "#b0b0b0",
    cursor: "pointer",
    transition: "all 0.3s ease",
    ":hover": {
      borderColor: "#8a2be2",
      color: "#ffffff",
    },
  },
  mobileRegisterButton: {
    flex: 1,
    padding: "0.8rem",
    background: "linear-gradient(135deg, #8a2be2 0%, #00bfff 100%)",
    border: "none",
    borderRadius: "5px",
    color: "white",
    cursor: "pointer",
    transition: "all 0.3s ease",
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 5px 15px rgba(138, 43, 226, 0.5)",
    },
  },

  // Hero Section with improved responsive image
  heroSection: {
    position: "relative",
    height: "100vh",
    minHeight: "700px",
    width: "100%",
    background: `
      linear-gradient(135deg, rgba(10, 10, 10, 0.9) 0%, rgba(20, 20, 30, 0.95) 100%), 
      url('https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80')
    `,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    padding: "80px 2rem 2rem",
    overflow: "hidden",
    "@media (max-width: 768px)": {
      minHeight: "600px",
      padding: "70px 1rem 1rem",
    },
  },
  heroContent: {
    maxWidth: "900px",
    padding: "rem",
    position: "relative",
    zIndex: 2,
    "@media (max-width: 768px)": {
      padding: "1rem",
    },
  },
  heroTitle: {
    fontSize: "clamp(1.2rem, 5vw, 4rem)",
    fontWeight: 500,
    marginBottom: "1.5rem",

    lineHeight: 1,
    background: "linear-gradient(135deg, #ffffff 0%, #b0b0b0 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  heroSubtitle: {
    fontSize: "clamp(1rem, 2vw, 1.5rem)",
    marginBottom: "3rem",
    fontWeight: 300,
    lineHeight: 1.6,
    color: "#b0b0b0",
    maxWidth: "700px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  heroButtons: {
    display: "flex",
    justifyContent: "center",
    gap: "1.5rem",
    flexWrap: "wrap",
    "@media (max-width: 480px)": {
      flexDirection: "column",
      gap: "1rem",
    },
  },
  // Hero Section

  // Updated Hero Section Styles
  heroSection: {
    position: "relative",
    height: "100vh",
    minHeight: "700px",
    width: "100%",
    background:
      "linear-gradient(135deg, rgba(10, 10, 10, 0.9) 0%, rgba(20, 20, 30, 0.95) 100%), url('https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80')",
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    padding: "80px 2rem 2rem",
    overflow: "hidden",
    "@media (max-width: 768px)": {
      minHeight: "600px",
      padding: "70px 1rem 1rem",
    },
  },
  heroContent: {
    maxWidth: "900px",
    padding: "1rem",
    position: "relative",
    zIndex: 2,
    "@media (max-width: 768px)": {
      padding: "1rem",
    },
  },
  heroTitle: {
    fontSize: "clamp(2rem, 5vw, 4rem)", // Responsive font size
    fontWeight: 800,
    marginBottom: "1.5rem",
    lineHeight: 1.2,
    background: "linear-gradient(135deg, #ffffff 0%, #b0b0b0 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    "@media (max-width: 480px)": {
      fontSize: "1.8rem", // Smaller font size for very small screens
      marginBottom: "1rem",
    },
  },
  heroSubtitle: {
    fontSize: "clamp(1rem, 2vw, 1.5rem)", // Responsive font size
    marginBottom: "2rem",
    fontWeight: 300,
    lineHeight: 1.6,
    color: "#b0b0b0",
    maxWidth: "700px",
    marginLeft: "auto",
    marginRight: "auto",
    "@media (max-width: 480px)": {
      fontSize: "1rem", // Smaller font size for very small screens
      marginBottom: "1.5rem",
    },
  },

  heroButtons: {
    display: "flex",
    justifyContent: "center",
    gap: "1rem",
    flexWrap: "wrap",
  },
  primaryButton: {
    padding: "1rem 2.5rem",
    fontSize: "1.1rem",
    fontWeight: 600,
    background: "linear-gradient(135deg, #8a2be2 0%, #00bfff 100%)",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 5px 20px rgba(138, 43, 226, 0.5)",
    ":hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 8px 25px rgba(138, 43, 226, 0.7)",
    },
  },
  secondaryButton: {
    padding: "1rem 2.5rem",
    fontSize: "1.1rem",
    fontWeight: 600,
    backgroundColor: "transparent",
    color: "#ffffff",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    ":hover": {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      transform: "translateY(-3px)",
      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.3)",
    },
  },
  scrollIndicator: {
    position: "absolute",
    bottom: "40px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "40px",
    height: "40px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "1.5rem",
    color: "rgba(255, 255, 255, 0.7)",
    cursor: "pointer",
    animation: "bounce 2s infinite",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "50%",
    transition: "all 0.3s ease",
    ":hover": {
      color: "#ffffff",
      borderColor: "rgba(255, 255, 255, 0.3)",
      transform: "translateX(-50%) translateY(-5px)",
    },
  },

  // Common Section Styles
  sectionContainer: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 2rem",
  },
  sectionTitle: {
    fontSize: "2.8rem",
    fontWeight: 700,
    textAlign: "center",
    marginBottom: "1.5rem",
    position: "relative",
    color: "#ffffff",
    "::after": {
      content: '""',
      display: "block",
      width: "80px",
      height: "4px",
      background: "linear-gradient(135deg, #8a2be2 0%, #00bfff 100%)",
      margin: "1.5rem auto",
      borderRadius: "2px",
    },
    "@media (max-width: 768px)": {
      fontSize: "2.2rem",
    },
  },
  sectionTitleAccent: {
    background: "linear-gradient(135deg, #8a2be2 0%, #00bfff 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  sectionSubtitle: {
    fontSize: "1.3rem",
    color: "#b0b0b0",
    textAlign: "center",
    maxWidth: "700px",
    margin: "0 auto 4rem",
    lineHeight: 1.6,
    fontWeight: 300,
    "@media (max-width: 768px)": {
      fontSize: "1.1rem",
      marginBottom: "3rem",
    },
  },

  // About Section
  aboutSection: {
    padding: "6rem 0",
    backgroundColor: "#111111",
    position: "relative",
    overflow: "hidden",
    "::before": {
      content: '""',
      position: "absolute",
      top: "-100px",
      right: "-100px",
      width: "300px",
      height: "300px",
      background:
        "radial-gradient(circle, rgba(138,43,226,0.15) 0%, rgba(0,0,0,0) 70%)",
      zIndex: 0,
    },
  },
  // Updated About Section Styles
  aboutContent: {
    display: "flex",
    gap: "4rem",
    alignItems: "center",
    position: "relative",
    zIndex: 1,
    "@media (max-width: 768px)": {
      flexDirection: "column-reverse", // Changed to column-reverse to put image on top
    },
  },
  aboutText: {
    flex: 1,
    fontSize: "1.1rem",
    lineHeight: 1.8,
    color: "#b0b0b0",
    "@media (max-width: 768px)": {
      marginTop: "2rem", // Add space between image and text on mobile
    },
  },
  aboutImage: {
    flex: 1,
    position: "relative",
    "@media (max-width: 768px)": {
      width: "100%",
      marginTop: 0, // Remove margin top since it's now on top
    },
  },
  aboutHeading: {
    fontSize: "2rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: "1.5rem",
    background: "linear-gradient(135deg, #8a2be2 0%, #00bfff 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    "@media (max-width: 768px)": {
      fontSize: "1.8rem",
    },
  },
  aboutParagraph: {
    marginBottom: "1.5rem",
  },
  featuresList: {
    marginTop: "2.5rem",
    paddingLeft: 0,
    listStyle: "none",
  },
  featureItem: {
    marginBottom: "1.2rem",
    fontSize: "1rem",
    display: "flex",
    alignItems: "center",
    color: "#d0d0d0",
  },
  featureIcon: {
    marginRight: "1rem",
    color: "#8a2be2",
    fontSize: "1.2rem",
  },

  aboutImageWrapper: {
    position: "relative",
    borderRadius: "15px",
    overflow: "hidden",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
  },
  aboutImageOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(135deg, rgba(138, 43, 226, 0.1) 0%, rgba(0, 191, 255, 0.1) 100%)",
  },
  aboutImg: {
    width: "100%",
    height: "auto",
    display: "block",
  },

  // Services Section
  servicesSection: {
    padding: "6rem 0",
    backgroundColor: "#0a0a0a",
    position: "relative",
    "::after": {
      content: '""',
      position: "absolute",
      bottom: "-100px",
      left: "-100px",
      width: "300px",
      height: "300px",
      background:
        "radial-gradient(circle, rgba(0,191,255,0.15) 0%, rgba(0,0,0,0) 70%)",
      zIndex: 0,
    },
  },
  servicesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "2.5rem",
    position: "relative",
    zIndex: 1,
    "@media (max-width: 480px)": {
      gridTemplateColumns: "1fr",
    },
  },
  serviceCard: {
    padding: "2.5rem",
    backgroundColor: "#1a1a1a",
    borderRadius: "15px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
    transition: "all 0.4s ease",
    textAlign: "center",
    border: "1px solid rgba(255, 255, 255, 0.05)",
    ":hover": {
      transform: "translateY(-10px)",
      boxShadow: "0 15px 40px rgba(138, 43, 226, 0.3)",
      borderColor: "rgba(138, 43, 226, 0.3)",
    },
  },
  serviceIconWrapper: {
    width: "80px",
    height: "80px",
    margin: "0 auto 1.5rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "2.5rem",
    background: "rgba(138, 43, 226, 0.1)",
    borderRadius: "50%",
    border: "1px solid rgba(138, 43, 226, 0.2)",
  },
  serviceIcon: {
    background: "linear-gradient(135deg, #8a2be2 0%, #00bfff 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  serviceTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: "#ffffff",
    marginBottom: "1.2rem",
  },
  serviceDescription: {
    fontSize: "1rem",
    color: "#b0b0b0",
    lineHeight: 1.6,
    marginBottom: "1.5rem",
  },
  serviceLearnMore: {
    background: "none",
    border: "none",
    color: "#8a2be2",
    fontSize: "1rem",
    fontWeight: 500,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    margin: "0 auto",
    transition: "all 0.3s ease",
    ":hover": {
      color: "#00bfff",
    },
  },

  // Contact Section
  contactSection: {
    padding: "6rem 0",
    backgroundColor: "#111111",
    position: "relative",
    overflow: "hidden",
  },
  contactContent: {
    display: "flex",
    gap: "4rem",
    "@media (max-width: 768px)": {
      flexDirection: "column",
    },
  },
  contactForm: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "1.8rem",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "0.8rem",
  },
  formLabel: {
    fontSize: "1rem",
    fontWeight: 500,
    color: "#ffffff",
  },
  formInput: {
    padding: "1.2rem",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "8px",
    fontSize: "1rem",
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    color: "#ffffff",
    transition: "all 0.3s ease",
    ":focus": {
      outline: "none",
      borderColor: "#8a2be2",
      boxShadow: "0 0 0 3px rgba(138, 43, 226, 0.2)",
      backgroundColor: "rgba(255, 255, 255, 0.05)",
    },
    "::placeholder": {
      color: "#555555",
    },
  },
  formTextarea: {
    padding: "1.2rem",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "8px",
    fontSize: "1rem",
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    color: "#ffffff",
    resize: "vertical",
    transition: "all 0.3s ease",
    ":focus": {
      outline: "none",
      borderColor: "#8a2be2",
      boxShadow: "0 0 0 3px rgba(138, 43, 226",
      boxShadow: "0 0 0 3px rgba(138, 43, 226, 0.2)",
      backgroundColor: "rgba(255, 255, 255, 0.05)",
    },
    "::placeholder": {
      color: "#555555",
    },
  },
  submitButton: {
    padding: "1.2rem",
    background: "linear-gradient(135deg, #8a2be2 0%, #00bfff 100%)",
    border: "none",
    borderRadius: "8px",
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#ffffff",
    cursor: "pointer",
    transition: "all 0.3s ease",
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 5px 20px rgba(138, 43, 226, 0.5)",
    },
  },
  contactInfo: {
    flex: 1,
    padding: "2rem",
    backgroundColor: "#1a1a1a",
    borderRadius: "15px",
    border: "1px solid rgba(255, 255, 255, 0.05)",
    "@media (max-width: 768px)": {
      marginTop: "2rem",
    },
  },
  contactInfoTitle: {
    fontSize: "1.8rem",
    fontWeight: 600,
    color: "#ffffff",
    marginBottom: "2rem",
    background: "linear-gradient(135deg, #8a2be2 0%, #00bfff 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  contactInfoItem: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    marginBottom: "1.5rem",
    color: "#b0b0b0",
    lineHeight: 1.6,
  },
  contactIcon: {
    fontSize: "1.5rem",
    minWidth: "30px",
    textAlign: "center",
    background: "linear-gradient(135deg, #8a2be2 0%, #00bfff 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  socialLinks: {
    display: "flex",
    gap: "1.5rem",
    marginTop: "3rem",
    flexWrap: "wrap",
  },
  socialLink: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    color: "#b0b0b0",
    textDecoration: "none",
    transition: "all 0.3s ease",
    ":hover": {
      color: "#ffffff",
    },
  },
  socialIcon: {
    fontSize: "1.2rem",
  },

  // Footer Styles
  footer: {
    backgroundColor: "#0a0a0a",
    padding: "5rem 0 0",
    borderTop: "1px solid rgba(255, 255, 255, 0.05)",
  },
  footerContent: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 2rem",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "3rem",
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr 1fr",
    },
    "@media (max-width: 480px)": {
      gridTemplateColumns: "1fr",
    },
  },
  footerSection: {
    marginBottom: "2rem",
  },
  footerLogo: {
    fontSize: "1.8rem",
    fontWeight: 700,
    marginBottom: "1.5rem",
    display: "flex",
    alignItems: "center",
  },
  footerText: {
    color: "#b0b0b0",
    lineHeight: 1.6,
    marginBottom: "1.5rem",
  },
  footerHeading: {
    fontSize: "1.3rem",
    fontWeight: 600,
    color: "#ffffff",
    marginBottom: "1.5rem",
    position: "relative",
    "::after": {
      content: '""',
      position: "absolute",
      bottom: "-8px",
      left: 0,
      width: "40px",
      height: "3px",
      background: "linear-gradient(135deg, #8a2be2 0%, #00bfff 100%)",
      borderRadius: "2px",
    },
  },
  footerLink: {
    display: "block",
    color: "#b0b0b0",
    marginBottom: "0.8rem",
    textDecoration: "none",
    transition: "all 0.3s ease",
    background: "none",
    border: "none",
    textAlign: "left",
    padding: 0,
    cursor: "pointer",
    ":hover": {
      color: "#ffffff",
      transform: "translateX(5px)",
    },
  },
  newsletterForm: {
    display: "flex",
    gap: "0.5rem",
    marginTop: "1rem",
  },
  newsletterInput: {
    flex: 1,
    padding: "0.8rem",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "6px",
    fontSize: "0.9rem",
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    color: "#ffffff",
    "::placeholder": {
      color: "#555555",
    },
  },
  newsletterButton: {
    padding: "0.8rem 1.5rem",
    background: "linear-gradient(135deg, #8a2be2 0%, #00bfff 100%)",
    border: "none",
    borderRadius: "6px",
    color: "#ffffff",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.3s ease",
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 5px 15px rgba(138, 43, 226, 0.3)",
    },
  },
  footerBottom: {
    padding: "2rem 0",
    textAlign: "center",
    borderTop: "1px solid rgba(255, 255, 255, 0.05)",
    marginTop: "3rem",
  },
  footerBottomText: {
    color: "#555555",
    fontSize: "0.9rem",
  },
};

export default Home;
